import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { TOAST_CONFIG } from '../../shared/Constants';
import {
  DeliveryChannel,
  NotificationChannel,
  ScheduledTask,
  ScheduleType,
  TaskApiError,
  TaskSchedule,
  updateTask,
} from '../../shared/services/scheduledTasksApi';
import { SlackGlyph } from './DeliveryChannels';
import { WEEKDAYS_PT } from './taskFormat';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.modal_background || theme.sidebar_background_solid || theme.sidebar_background};
  border: 1px solid ${({ theme }) => theme.modal_border || theme.sidebar_border};
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 92%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.font_color};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  font-family: 'Noka', sans-serif;
  margin: 0;
`;

const CloseBtn = styled.button`
  all: unset;
  display: flex;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  &:hover { color: ${({ theme }) => theme.font_color}; }
`;

const Label = styled.label`
  display: block;
  font-size: 0.86rem;
  font-weight: 600;
  margin: 18px 0 6px;
`;

const Hint = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 5px;
  line-height: 1.4;
`;

const fieldCss = `
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 10px;
  background: transparent;
  font-size: 0.93rem;
  outline: none;
  font-family: inherit;
`;

const Input = styled.input`
  ${fieldCss}
  width: 100%;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  color: ${({ theme }) => theme.font_color};
  color-scheme: ${({ theme }) => (theme.mode === 'dark' ? 'dark' : 'light')};
  &:focus { border-color: ${({ theme }) => theme.font_color}; }
`;

const TextArea = styled.textarea`
  ${fieldCss}
  width: 100%;
  min-height: 120px;
  resize: vertical;
  line-height: 1.45;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  color: ${({ theme }) => theme.font_color};
  &:focus { border-color: ${({ theme }) => theme.font_color}; }
`;

const Segmented = styled.div`
  display: inline-flex;
  padding: 3px;
  border-radius: 11px;
  background: ${({ theme }) => theme.hover_overlay_light};
  gap: 2px;
  flex-wrap: wrap;
`;

const SegBtn = styled.button<{ $on: boolean }>`
  all: unset;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.84rem;
  font-weight: 550;
  cursor: pointer;
  color: ${({ $on, theme }) => ($on ? theme.font_color : theme.text_secondary)};
  background: ${({ $on, theme }) => ($on ? theme.modal_background || theme.sidebar_background_solid : 'transparent')};
  box-shadow: ${({ $on }) => ($on ? '0 1px 3px rgba(0,0,0,0.15)' : 'none')};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-top: 10px;
`;

const Chip = styled.button<{ $on: boolean }>`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 0.84rem;
  cursor: pointer;
  border: 1px solid ${({ $on, theme }) => ($on ? theme.font_color : theme.sidebar_border)};
  background: ${({ $on, theme }) => ($on ? theme.hover_overlay_light : 'transparent')};
  color: ${({ theme }) => theme.font_color};
  &:disabled { cursor: default; opacity: 0.75; }
  & svg { font-size: 1rem; }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 26px;
`;

const CancelBtn = styled.button`
  all: unset;
  padding: 9px 16px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
`;

const SaveBtn = styled.button`
  all: unset;
  padding: 9px 18px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  background: ${({ theme }) => theme.font_color};
  color: ${({ theme }) => theme.sidebar_background_solid || theme.sidebar_background};
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const TYPES: { value: ScheduleType; label: string }[] = [
  { value: 'once', label: 'Uma vez' },
  { value: 'daily', label: 'Diária' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensal' },
];

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const isSlack = (entry: string) => entry === 'slack' || entry.startsWith('slack:');

interface TaskEditModalProps {
  task: ScheduledTask | null;
  slackChannels: NotificationChannel[];
  onClose: () => void;
  onSaved: (task: ScheduledTask) => void;
}

/** Edição manual de uma tarefa (a criação acontece pelo chat). */
const TaskEditModal: React.FC<TaskEditModalProps> = ({ task, slackChannels, onClose, onSaved }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [type, setType] = useState<ScheduleType>('daily');
  const [time, setTime] = useState('08:00');
  const [weekday, setWeekday] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [date, setDate] = useState(todayIso());
  const [email, setEmail] = useState(false);
  const [slack, setSlack] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!task) return;
    setTitle(task.title);
    setPrompt(task.prompt);
    setType(task.schedule.type);
    setTime(task.schedule.time || '08:00');
    setWeekday(typeof task.schedule.weekday === 'number' ? task.schedule.weekday : 0);
    setDayOfMonth(task.schedule.day_of_month || 1);
    setDate(task.schedule.date || todayIso());
    setEmail(task.delivery.includes('email'));
    setSlack(task.delivery.filter(isSlack));
  }, [task]);

  useEffect(() => {
    if (!task) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [task, onClose]);

  if (!task) return null;

  // Destinos do Slack: os verificados + qualquer um já usado pela tarefa (para
  // não sumir da lista um destino legado/desconectado que ainda está marcado).
  const slackOptions = Array.from(new Set([
    ...slackChannels.filter((c) => c.verified).map((c) => `slack:${c.alias}`),
    ...task.delivery.filter(isSlack),
  ]));

  const toggleSlack = (entry: string) =>
    setSlack((prev) => (prev.includes(entry) ? prev.filter((e) => e !== entry) : [...prev, entry]));

  const canSave = title.trim().length > 0 && prompt.trim().length > 0 && !!time && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    const schedule: TaskSchedule = { type, time };
    if (type === 'weekly') schedule.weekday = weekday;
    if (type === 'monthly') schedule.day_of_month = dayOfMonth;
    if (type === 'once') schedule.date = date;
    const delivery = ['in_app', ...(email ? ['email'] : []), ...slack] as DeliveryChannel[];
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const updated = await updateTask(token, task.task_id, {
        title: title.trim(),
        prompt: prompt.trim(),
        schedule,
        delivery,
      });
      toast.success('Tarefa atualizada', { ...TOAST_CONFIG, autoClose: 2000 });
      onSaved(updated);
      onClose();
    } catch (e) {
      toast.error(e instanceof TaskApiError ? e.message : 'Não foi possível salvar a tarefa.', TOAST_CONFIG);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Editar tarefa">
        <Header>
          <Title>Editar tarefa</Title>
          <CloseBtn onClick={onClose} aria-label="Fechar"><CloseIcon /></CloseBtn>
        </Header>

        <Label htmlFor="task-title">Título</Label>
        <Input id="task-title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />

        <Label htmlFor="task-prompt">Instrução</Label>
        <TextArea id="task-prompt" value={prompt} maxLength={8000} onChange={(e) => setPrompt(e.target.value)} />
        <Hint>Cada execução roda sozinha, sem o histórico da conversa — descreva tudo o que o assistente precisa saber.</Hint>

        <Label>Quando</Label>
        <Segmented role="radiogroup" aria-label="Frequência">
          {TYPES.map((t) => (
            <SegBtn key={t.value} type="button" role="radio" aria-checked={type === t.value} $on={type === t.value} onClick={() => setType(t.value)}>
              {t.label}
            </SegBtn>
          ))}
        </Segmented>
        <Row>
          {type === 'once' && (
            <Input type="date" value={date} min={todayIso()} onChange={(e) => setDate(e.target.value)} style={{ width: 170 }} aria-label="Data" />
          )}
          {type === 'monthly' && (
            <Input
              type="number"
              min={1}
              max={28}
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(Math.min(28, Math.max(1, Number(e.target.value) || 1)))}
              style={{ width: 90 }}
              aria-label="Dia do mês"
            />
          )}
          <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ width: 120 }} aria-label="Horário" />
          <Hint style={{ marginTop: 0 }}>Horário de Brasília</Hint>
        </Row>
        {type === 'weekly' && (
          <Row>
            {WEEKDAYS_PT.map((d, i) => (
              <Chip key={d} type="button" $on={weekday === i} onClick={() => setWeekday(i)}>
                {d.slice(0, 3)}
              </Chip>
            ))}
          </Row>
        )}
        {type === 'monthly' && <Hint>Dias 1 a 28, para funcionar em todos os meses.</Hint>}

        <Label>Entrega</Label>
        <Row style={{ marginTop: 0 }}>
          <Chip type="button" $on disabled title="A conversa da tarefa sempre recebe o resultado">
            <ChatBubbleOutlineIcon /> Conversa
          </Chip>
          <Chip type="button" $on={email} onClick={() => setEmail((v) => !v)}>
            <MailOutlineIcon /> E-mail
          </Chip>
          {slackOptions.map((entry) => (
            <Chip key={entry} type="button" $on={slack.includes(entry)} onClick={() => toggleSlack(entry)}>
              <SlackGlyph size={15} /> {entry === 'slack' ? 'Slack' : entry.slice('slack:'.length)}
            </Chip>
          ))}
        </Row>
        {slackOptions.length === 0 && (
          <Hint>Para receber no Slack, conecte um webhook em "Canais de entrega", no fim da página.</Hint>
        )}

        <Footer>
          <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
          <SaveBtn onClick={handleSave} disabled={!canSave}>{saving ? 'Salvando…' : 'Salvar'}</SaveBtn>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default TaskEditModal;
